import { useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase-config'
import { isWorkoutDoc } from '../utils/workout'

/**
 * Loads a workout doc plus every exercise it references.
 *
 * Also handles links from before the exercise/workout split: `/workouts/:id`
 * used to point at a single exercise, and the migration keeps document IDs, so
 * if the id turns out to name an exercise we report it via `legacyExerciseId`
 * and the caller redirects.
 */
export const useWorkout = (id) => {
  const [state, setState] = useState({
    workout: null,
    exercisesById: {},
    loading: true,
    error: null,
    legacyExerciseId: null
  })

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setState({
        workout: null,
        exercisesById: {},
        loading: true,
        error: null,
        legacyExerciseId: null
      })

      try {
        const workoutSnap = await getDoc(doc(db, 'workouts', id))
        const data = workoutSnap.exists() ? workoutSnap.data() : null

        if (!data || !isWorkoutDoc(data)) {
          // Either nothing here, or a leftover exercise doc in the old
          // collection. Both cases point at the exercise page if one matches.
          const exerciseSnap = await getDoc(doc(db, 'exercises', id))
          if (cancelled) return

          if (exerciseSnap.exists() || data) {
            setState(prev => ({ ...prev, loading: false, legacyExerciseId: id }))
          } else {
            setState(prev => ({ ...prev, loading: false, error: 'Workout not found' }))
          }
          return
        }

        const workout = { id: workoutSnap.id, ...data }

        // Pull in each referenced exercise once, even if it appears twice.
        const uniqueIds = [...new Set(
          workout.items.map(item => item.exerciseId).filter(Boolean)
        )]
        const snapshots = await Promise.all(
          uniqueIds.map(exerciseId => getDoc(doc(db, 'exercises', exerciseId)))
        )
        if (cancelled) return

        const exercisesById = {}
        snapshots.forEach(snapshot => {
          if (snapshot.exists()) {
            exercisesById[snapshot.id] = { id: snapshot.id, ...snapshot.data() }
          }
        })

        setState({
          workout,
          exercisesById,
          loading: false,
          error: null,
          legacyExerciseId: null
        })
      } catch (err) {
        console.error('Error loading workout:', err)
        if (cancelled) return
        setState(prev => ({ ...prev, loading: false, error: 'Error loading workout' }))
      }
    }

    if (id) load()

    return () => { cancelled = true }
  }, [id])

  return state
}

export default useWorkout
